import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Link } from 'react-router-dom';
import { fetchBannersBySection } from '../../../services/bannerService';
import { initBannerSwiper } from '../../../utils/swiperBannerInit';
import './HeroSection.css';
import { Autoplay, Navigation, Pagination } from 'swiper';

const FALLBACK_SLIDES = [
  {
    title: 'Your Business. One Flow.',
    subtitle: 'Collaborate and manage all your tasks in one place, and avoid missing deadlines.',
    ctaText: 'Start for free',
    ctaLink: '/register',
    secondaryText: 'Learn more',
    secondaryLink: '/about-us',
  },
  {
    title: 'Simplifying Business, Empowering Growth',
    subtitle: 'Reliable, secure, and cost effective platform to manage all your tasks in one place.',
    ctaText: 'Try it now',
    ctaLink: '/register',
    secondaryText: 'See products',
    secondaryLink: '/products',
  },
];

const HeroSection = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBannersBySection('Home Page').then((data) => {
      setBanners(data || []);
      setLoading(false);
    });
  }, []);

  const useFallback = !loading && banners.length === 0;
  const slideCount = useFallback ? FALLBACK_SLIDES.length : banners.length;
  const multiSlide = slideCount > 1;
  // Loop + React Swiper can break prev/next with only 2 slides; loop needs 3+ duplicated slides to behave reliably
  const useLoop = multiSlide && slideCount > 2;

  // Do not mount Swiper until we know slides (avoids 0 slides → N slides update breaking loop / first slide)
  if (loading) {
    return (
      <section className="hero-section hero-section--banner" aria-busy="true">
        <div className="hero-banner-wrapper hero-banner-loading">
          <div className="hero-banner-loading-inner" />
        </div>
      </section>
    );
  }

  const swiperKey = useFallback
    ? 'fallback'
    : banners.map((b) => b.id).join('-');

  return (
    <section className="hero-section hero-section--banner">
      <div className="hero-banner-wrapper">
      <Swiper
        key={swiperKey}
        initialSlide={0}
        spaceBetween={0}
        slidesPerView={1}
        loop={useLoop}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={multiSlide ? { clickable: true } : false}
        navigation={multiSlide}
        className="hero-swiper hero-swiper--banner"
        modules={[Autoplay, Navigation, Pagination]}
        onSwiper={initBannerSwiper}
      >
        {useFallback ? (
          FALLBACK_SLIDES.map((slide, idx) => (
            <SwiperSlide key={idx}>
              <div className="hero-banner-slide hero-banner-slide--fallback">
                <div className="hero-banner-content">
                  <h1 className="hero-title">{slide.title}</h1>
                  <p className="hero-subtitle">{slide.subtitle}</p>
                  <div className="hero-actions">
                    <Link to={slide.ctaLink} className="btn btn-primary hero-cta">
                      {slide.ctaText}
                    </Link>
                    <Link
                      to={slide.secondaryLink}
                      className="btn btn-secondary hero-cta-secondary"
                      style={{ color: '#ffffff' }}
                    >
                      {slide.secondaryText}
                    </Link>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))
        ) : (
          banners.map((banner) => (
            <SwiperSlide key={banner.id}>
              <div className="hero-banner-slide">
                <picture>
                  {banner.imageMobileUrl && (
                    <source
                      media="(max-width: 768px)"
                      srcSet={banner.imageMobileUrl}
                    />
                  )}
                  <img
                    src={banner.imageDesktopUrl}
                    alt={banner.name}
                    className="hero-banner-img"
                  />
                </picture>
                <div className="hero-banner-overlay">
                  <div className="hero-banner-content">
                    {banner.main_heading_text && (
                      <h1
                        className="hero-title"
                        style={{ color: banner.main_heading_text_color || '#ffffff' }}
                      >
                        {banner.main_heading_text}
                      </h1>
                    )}
                    {banner.sub_heading_text && (
                      <p
                        className="hero-subtitle"
                        style={{ color: banner.sub_heading_text_color || 'rgba(255,255,255,0.9)' }}
                      >
                        {banner.sub_heading_text}
                      </p>
                    )}
                    {(banner.button_text && banner.button_action_url) && (
                      <div className="hero-actions">
                        <Link
                          to={banner.button_action_url}
                          className="btn hero-banner-cta"
                          style={{
                            backgroundColor: banner.button_background_color || '#0066cc',
                            color: banner.button_text_color || '#ffffff',
                          }}
                        >
                          {banner.button_text}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))
        )}
      </Swiper>
      </div>
    </section>
  );
};

export default HeroSection;
